import type React from 'react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import useEmblaCarousel from 'embla-carousel-react';

import { CarouselButtons } from 'app/ui/components/molecules/carousel-modal/carousel-buttons';
import { Slide } from 'app/ui/components/molecules/carousel-modal/slide';
import type { CarouselModalConfig } from 'app/ui/components/molecules/carousel-modal/types';
import CloseIcon from 'app/assets/close-icon.svg';
import { isMobileDevice } from 'app/utils/is-mobile';

interface CarouselModalProps {
  config: CarouselModalConfig;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onFinalAction?: () => void | Promise<void>;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme: { spacing } }) => spacing(4)};
`;

const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90vw;
  max-width: 100%;
  max-height: 80vh;
  
  ${({ theme: { media } }) => `
    ${media.small(
      () => `
        width: 80vw;
      `,
    )}
    ${media.medium(
      () => `
        width: 70vw;
      `,
    )}
    ${media.large(
      () => `
        width: 80vw;
      `,
    )}
  `}
`;

const CarouselContainer = styled.div`
  overflow: hidden;
  width: 100%;
  border-radius: ${({ theme: { spacing } }) => spacing(3)};
  box-shadow: 0 0 8px 0 rgba(255, 237, 0, 0.75);
  background-color: ${({ theme: { colors } }) => colors.darkGrey};
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${({ theme: { spacing } }) => spacing(4)};
  right: ${({ theme: { spacing } }) => spacing(4)};
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 10;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
  }
`;

const CarouselViewport = styled.div`
  overflow: hidden;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const CarouselSlideContainer = styled.div`
  display: flex;
  touch-action: auto;
  height: 100%;
`;

const CarouselSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  position: relative;
  display: flex;
`;

const DotsContainer = styled.div`
  display: flex;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  margin-top: ${({ theme: { spacing } }) => spacing(3)};
  position: absolute;
  bottom: -${({ theme: { spacing } }) => spacing(8)};
  
  ${({ theme: { media, spacing } }) => `
    ${media.small(
      () => `
        margin-top: ${spacing(6)};
        bottom: -${spacing(10)};
      `,
    )}
  `}
`;

interface DotButtonProps {
  $isActive: boolean;
}

const DotButton = styled.button<DotButtonProps>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 0;
  transition: background-color 0.3s ease;
  background-color: ${({ $isActive, theme: { colors } }) => ($isActive ? colors.focoltone : colors.knop)};

  &:hover {
    background-color: ${({ $isActive, theme: { colors } }) => ($isActive ? colors.focoltone : colors.lightGray)};
  }
`;

export const CarouselModal: React.FC<CarouselModalProps> = ({
  config,
  isOpen: controlledIsOpen,
  onOpenChange,
  onFinalAction,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [current, setCurrent] = useState(0);
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;

  const { slides, storageKey, maxShowTimes = 3, disableOnMobile = true } = config;
  const isMobile = useMemo(() => isMobileDevice(), []);

  useEffect(() => {
    if (controlledIsOpen === undefined) {
      const carouselShownTimes = Number(localStorage.getItem(storageKey)) || 0;
      const dontShowAgain = carouselShownTimes >= maxShowTimes;

      if ((!disableOnMobile || !isMobile) && !dontShowAgain) {
        setInternalIsOpen(true);
      } else {
        setInternalIsOpen(false);
      }
    }
  }, [isMobile, storageKey, maxShowTimes, disableOnMobile, controlledIsOpen]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onSelect = () => {
      setCurrent(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const handleDotClick = useCallback(
    (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
      }
    },
    [emblaApi],
  );

  const handleNextClick = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  const handlePreviousClick = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const handleComplete = useCallback(() => {
    setIsOpen(false);
    const carouselShownTimes = Number(localStorage.getItem(storageKey)) || 0;
    localStorage.setItem(storageKey, String(carouselShownTimes + 1));
    if (onFinalAction) {
      onFinalAction();
    }
  }, [setIsOpen, storageKey, onFinalAction]);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleDismiss();
      }
    },
    [handleDismiss],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <CarouselContainer>
          <CloseButton onClick={handleDismiss} aria-label="Close carousel modal">
            <img src={CloseIcon} alt="Close" />
          </CloseButton>
          <CarouselViewport ref={emblaRef}>
            <CarouselSlideContainer>
              {slides.map((slide, index) => (
                <CarouselSlide key={`slide-${slide.title}-${index}`}>
                  <Slide
                    slide={slide}
                    buttons={
                      <CarouselButtons
                        onNext={handleNextClick}
                        onPrevious={handlePreviousClick}
                        isFirst={current === 0}
                        isLast={current === slides.length - 1}
                        onDone={handleComplete}
                      />
                    }
                  />
                </CarouselSlide>
              ))}
            </CarouselSlideContainer>
          </CarouselViewport>
        </CarouselContainer>
        <DotsContainer>
          {slides.map((slide, index) => (
            <DotButton
              key={`dot-${slide.title}-${index}`}
              $isActive={index === current}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </DotsContainer>
      </ModalContainer>
    </Overlay>
  );
};
